import React, { useEffect } from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { VideoPlayerModal } from "@/components/ui/VideoPlayerModal";
import { InlineVideoPlayer } from "@/components/ui/InlineVideoPlayer";

interface VideoPlayerProps {
  mediaItem: MediaItem;
  isOpen: boolean;
  onClose: () => void;
  getStreamUrlByFileId?: (fileId: string) => string | undefined;
  clientId?: string;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  mediaItem,
  isOpen,
  onClose,
  getStreamUrlByFileId: externalGetStreamUrl,
  clientId,
}) => {
  const [isMobile, setIsMobile] = React.useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(
        window.innerWidth <= 768 ||
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent,
          ),
      );
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  if (!isOpen) return null;

  const containerStyles = isMobile
    ? {
        height: "100vh",
        width: "100vw",
      }
    : {
        maxHeight: "90vh",
        maxWidth: "calc(90vh * 9 / 16)",
        aspectRatio: "9 / 16",
      };

  const videoContainerStyles = {
    maxHeight: isMobile ? "100vh" : "90vh",
    maxWidth: isMobile ? "100vw" : "calc(90vh * 9 / 16)",
    aspectRatio: "9 / 16",
    width: "100%",
    height: "100%",
  };

  return (
    <VideoPlayerModal isOpen={isOpen} onClose={onClose} size="lg">
      <div
        className={`bg-gray-900 overflow-hidden shadow-2xl flex flex-col ${
          isMobile ? "fixed inset-0 z-50 rounded-none" : "rounded-lg"
        }`}
        style={containerStyles}
      >
        <div
          className="relative bg-black overflow-hidden"
          style={videoContainerStyles}
        >
          {/* Header Overlay */}
          <div className="absolute top-0 left-0 right-0 z-20 p-4 bg-linear-to-b from-black/80 via-black/40 to-transparent pointer-events-none">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="text-white">
                  <h3 className="font-medium text-lg">
                    {mediaItem.originalFilename?.replace(/\.[^/.]+$/, "") ||
                      "Video"}
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">
                    {mediaItem.mediaType === "video" &&
                    mediaItem.frontendData?.duration
                      ? `${Math.floor(mediaItem.frontendData.duration / 60)}:${String(mediaItem.frontendData.duration % 60).padStart(2, "0")}`
                      : "Video"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 pointer-events-auto">
                <button
                  onClick={onClose}
                  className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full"
                  aria-label="Close"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Video Element */}
          {clientId && (
            <InlineVideoPlayer
              mediaItem={mediaItem}
              autoPlay={false}
              objectFit="contain"
              getStreamUrlByFileId={externalGetStreamUrl}
            />
          )}
        </div>
      </div>
    </VideoPlayerModal>
  );
};
